/**
 * The door to the prospects pages: who may open them, and the brand filter
 * they all share.
 *
 * `ProspectingLayout` is the route element every `/prospecting/*` page and the
 * Settings > Prospecting tab render inside (routes.tsx). It asks GET /me once,
 * through the gateway, and then:
 *
 *   owner, or a member with a seat   the page, inside the brand filter's provider
 *   signed in, no seat               one sentence saying so, and who to ask
 *   signed out                       nothing: the session is already dropped, and
 *                                    `RequireAuth` is on its way to the sign-in screen
 *   no service, or it did not answer the reason, with a way to try again
 *
 * This is tidiness, like every gate in the console: the service refuses a
 * person with no seat whatever this draws, and the gateway refuses anybody it
 * did not sign in.
 *
 * Replaces the old app's `NOT_ALLOWED` page (radar `src/proxy.ts`), which was
 * a page of its own with the old app's name on it.
 */
import type { ReactNode } from 'react'
import { Outlet } from 'react-router-dom'
import { Button } from '../../../components/Button'
import { EmptyState, ErrorState, LoadingState } from '../../../components/States'
import { Page } from '../../../components/Page'
import { IconLock, IconRefresh } from '../../../components/icons'
import { SettingsSection } from '../../settings/SettingsSection'
import { NO_ACCESS_MESSAGE } from './api'
import { accessFrom, type ProspectingAccess as Access } from './access-model'
import { IconTarget } from './icons'
import { ProspectingBrandProvider, useProspectingMe } from './useProspectingBrand'

/** Who may open the prospects pages, for the pages and the sidebar alike. */
export function useProspectingAccess(): Access & { retry: () => void } {
  const query = useProspectingMe()
  return { ...accessFrom(query), retry: () => void query.refetch() }
}

/**
 * Where the gate draws a refusal: a page of its own in the shell, or a
 * section of the Settings page, so it sits in the frame around it.
 */
export type AccessFrame = 'page' | 'section'

function Framed({ frame, children }: { frame: AccessFrame; children: ReactNode }) {
  if (frame === 'section') {
    return (
      <SettingsSection title="Prospecting" description="Reply drafts, people and brands, the writing schedule and everything the searches find.">
        {children}
      </SettingsSection>
    )
  }
  return (
    <Page icon={<IconTarget />} title="Prospecting">
      {children}
    </Page>
  )
}

/** The gate alone, for a caller that lays out its own content. */
export function ProspectingAccess({ frame = 'page', children }: { frame?: AccessFrame; children: ReactNode }) {
  const access = useProspectingAccess()
  switch (access.state) {
    case 'allowed':
      return <>{children}</>
    case 'loading':
    case 'signed-out':
      return <LoadingState label="Opening Prospecting" />
    case 'no-access':
      return (
        <Framed frame={frame}>
          <EmptyState variant="card" icon={<IconLock />} title="Prospecting is not on this account" description={NO_ACCESS_MESSAGE} />
        </Framed>
      )
    case 'unavailable':
    case 'failed':
      return (
        <Framed frame={frame}>
          <ErrorState error={new Error(access.message)} title="Prospecting could not be opened">
            <div className="pt-1">
              <Button size="sm" variant="secondary" onClick={access.retry} leadingIcon={<IconRefresh className="h-3.5 w-3.5" />}>
                Try again
              </Button>
            </div>
          </ErrorState>
        </Framed>
      )
  }
}

/** The route element: the gate, then the brand filter's provider, then the page. */
export function ProspectingLayout({ frame = 'page' }: { frame?: AccessFrame }) {
  return (
    <ProspectingAccess frame={frame}>
      <ProspectingBrandProvider>
        <Outlet />
      </ProspectingBrandProvider>
    </ProspectingAccess>
  )
}
